import React from 'react';

import * as Yup from 'yup';

import { ia, iaRa } from 'lib/helpers/utility';

import { PAYER_OPTIONS } from 'components/shared/Filters/lib/filterOptions';

import Actions from '../components/Actions';
import PayerNameWithInfo from '../components/PayerNameWithInfo';

const PAYER_KINDS = [
  { label: 'In-network', value: 0 },
  { label: 'Out-of-network', value: 1 },
  { label: 'Unknown', value: 2 }
];

const getProviderCount = ({ value }) => {
  if (!ia(value)) {
    return '-';
  }

  const count = value.length;

  return `${count} ${count === 1 ? 'provider' : 'providers'}`;
};

export const defaultColumns = (practiceId) => [
  {
    field: 'id',
    headerName: 'ID',
    cellClass: 'no-border'
  },
  {
    field: 'name',
    headerName: 'Name',
    cellClass: 'no-border',
    cellRenderer: (params) => <PayerNameWithInfo payer={params.data} />
  },
  {
    field: 'in_network_providers',
    headerName: 'In-network',
    cellClass: 'no-border',
    valueFormatter: getProviderCount
  },
  {
    field: 'out_of_network_providers',
    headerName: 'Out of network',
    cellClass: 'no-border',
    valueFormatter: getProviderCount
  },
  {
    field: 'actions',
    headerName: 'Actions',
    cellClass: 'no-border',
    cellRenderer: (params) => <Actions data={params.data} practiceId={practiceId} />,
    maxWidth: 88,
    minWidth: 36,
    resizable: false,
    sortable: false,
    pinned: 'right'
  }
];

export const getDefaultFilters = (practiceId) => ({
  search: {
    type: 'search',
    fields: [
      { field: 'id', label: 'ID' },
      { field: 'name', label: 'Name' }
    ]
  },
  name: {
    ...PAYER_OPTIONS,
    ...(practiceId != null && { params: { practiceId } })
  },
  address: {
    title: 'Address',
    allowed_operators: ['contains', 'does not contain'],
    icon: 'location-mono'
  },
  network_status: {
    title: 'Network status',
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: PAYER_KINDS.filter((kind) =>
          kind.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'payer_network_statuses.options',
    icon: 'list-unordered-linear',
    allowed_operators: ['is', 'is not']
  }
});

export const PayerInitialValues = (payer, addressData) => ({
  name: payer?.name || '',
  phone: payer?.phone || '',
  address_ln_1: addressData?.address?.address_ln_1 || addressData?.address_ln_1 || '',
  address_ln_2: addressData?.address?.address_ln_2 || addressData?.address_ln_2 || '',
  city: addressData?.address?.city || addressData?.city || '',
  state: addressData?.address?.state || addressData?.state || null,
  zip: addressData?.address?.zip || addressData?.zip || '',
  virtual_modifiers:
    addressData?.payer?.virtual_modifiers || payer?.practicePayers?.[0]?.virtual_modifiers || [],
  type: addressData?.payer?.type || payer?.practicePayers?.[0]?.type || '',
  in_network_providers: iaRa(payer?.in_network_providers).map((p) => ({
    provider_id: p?.provider_id ?? p?.id,
    state_ids: iaRa(p?.state_ids)
  })),
  out_of_network_providers: iaRa(payer?.out_of_network_providers).map((p) => ({
    provider_id: p?.provider_id ?? p?.id,
    state_ids: iaRa(p?.state_ids)
  }))
});

export const PayerValidationSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, 'Payer name must be at least 3 characters')
    .required('Payer name is required')
});
